import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Offcanvas,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
} from 'react-bootstrap';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  type Connection,
  type EdgeChange,
  type NodeChange,
} from '@xyflow/react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import '@xyflow/react/dist/style.css';
import { supabase } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry';
import { SupplierComplianceDocuments } from '../components/supplyChain/SupplierComplianceDocuments';
import { CountrySelect } from '../components/common/CountrySelect';
import DropdownSelect from '../components/common/DropdownSelect';
import SupplierNodeComponent from '../components/supplyChain/SupplierNode';
import {
  useSupplyChain,
  type CreateSupplyChainSupplierInput,
  type SupplyChainEdge,
  type SupplyChainNode,
} from '../hooks/useSupplyChain';
import type {
  DropdownSelectOption,
  SupplyChainSupplierDocument,
  UserOrganisation,
} from '../types/types';
import { getSupplierAnalysisProblems } from '../util/supplyChainAnalysis';

const statusColorBySupplierStatus: Record<string, string> = {
  Unverified: '#94a3b8',
  Warning: '#f59e0b',
  Verified: '#22c55e',
  Flagged: '#ef4444',
  Expired: '#f97316',
};

const complianceStatusOptions = [
  'Unverified',
  'Warning',
  'Verified',
  'Flagged',
  'Expired',
] as const;

type TierFilterValue = '0' | '1' | '2' | '3' | 'all';

const initialFormState: CreateSupplyChainSupplierInput = {
  parent_id: null,
  parent_ids: [],
  company_name: '',
  registration_number: '',
  duns_number: null,
  email: null,
  phone: null,
  country: null,
  city: null,
  address: null,
};

ChartJS.register(ArcElement, Tooltip, Legend);

const SupplyChain = () => {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;
  const organisationId = selectedOrganisation?.organisation?.id ?? null;
  const hasSupplyChainMapping =
    selectedOrganisation?.organisation?.plan?.features?.['supplyChainMapping'];

  const {
    suppliers,
    nodes: fetchedNodes,
    edges: fetchedEdges,
    loading,
    saving,
    error,
    createSupplier,
    updateSupplier,
    refetch,
  } = useSupplyChain(selectedOrganisation?.organisation?.id);

  const [nodes, setNodes] = useState(fetchedNodes);
  const [edges, setEdges] = useState(fetchedEdges);
  const [showCreateSupplierModal, setShowCreateSupplierModal] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(
    null,
  );
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(
    null,
  );
  const [showDeleteSupplierModal, setShowDeleteSupplierModal] = useState(false);
  const [deleteWithSubSuppliers, setDeleteWithSubSuppliers] = useState(false);
  const [deleteSupplierError, setDeleteSupplierError] = useState<string | null>(
    null,
  );
  const [deletingSupplier, setDeletingSupplier] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState<TierFilterValue>('all');
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [parentSupplierSearch, setParentSupplierSearch] = useState('');
  const [showParentSuppliersDrawer, setShowParentSuppliersDrawer] =
    useState(false);
  const [analysisDocuments, setAnalysisDocuments] = useState<
    SupplyChainSupplierDocument[]
  >([]);
  const [formState, setFormState] =
    useState<CreateSupplyChainSupplierInput>(initialFormState);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim().toLowerCase());
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  const selectedSupplier = useMemo(
    () =>
      suppliers.find((supplier) => supplier.id === selectedSupplierId) ?? null,
    [selectedSupplierId, suppliers],
  );

  const selectedSupplierParentIds = useMemo(
    () =>
      selectedSupplier
        ? Array.from(
            new Set(
              edges
                .filter((edge) => edge.target === selectedSupplier.id)
                .map((edge) => edge.source),
            ),
          )
        : [],
    [edges, selectedSupplier],
  );

  const selectedSupplierParents = useMemo(
    () =>
      suppliers.filter((supplier) =>
        selectedSupplierParentIds.includes(supplier.id),
      ),
    [selectedSupplierParentIds, suppliers],
  );

  const selectedSupplierChildIds = useMemo(
    () =>
      selectedSupplier
        ? Array.from(
            new Set(
              edges
                .filter((edge) => edge.source === selectedSupplier.id)
                .map((edge) => edge.target),
            ),
          )
        : [],
    [edges, selectedSupplier],
  );

  const parentSupplierOptions = useMemo<DropdownSelectOption[]>(
    () => [
      { id: '__none__', label: 'No parent supplier' },
      ...suppliers
        .filter((supplier) => supplier.id !== editingSupplierId)
        .map((supplier) => ({
          id: supplier.id,
          label: supplier.company_name,
        })),
    ],
    [editingSupplierId, suppliers],
  );
  const editableParentSuppliers = useMemo(
    () => suppliers.filter((supplier) => supplier.id !== editingSupplierId),
    [editingSupplierId, suppliers],
  );
  const filteredEditableParentSuppliers = useMemo(() => {
    const query = parentSupplierSearch.trim().toLowerCase();

    if (!query) {
      return editableParentSuppliers;
    }

    return editableParentSuppliers.filter((supplier) =>
      supplier.company_name.toLowerCase().includes(query),
    );
  }, [editableParentSuppliers, parentSupplierSearch]);
  const selectedFormParentSupplierNames = useMemo(
    () =>
      suppliers
        .filter((supplier) => formState.parent_ids.includes(supplier.id))
        .map((supplier) => supplier.company_name),
    [formState.parent_ids, suppliers],
  );

  const selectedSupplierStatus =
    selectedSupplier?.compliance_status ?? 'Unverified';
  const selectedSupplierStatusColor =
    statusColorBySupplierStatus[selectedSupplierStatus] ??
    statusColorBySupplierStatus.Unverified;
  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    statusFilter !== 'all' ||
    tierFilter !== 'all';

  const supplierDepthById = useMemo(() => {
    const supplierById = new Map(
      suppliers.map((supplier) => [supplier.id, supplier]),
    );
    const depthById = new Map<string, number>();

    const getDepth = (
      supplierId: string,
      visited = new Set<string>(),
    ): number => {
      const cachedDepth = depthById.get(supplierId);
      if (cachedDepth !== undefined) {
        return cachedDepth;
      }

      if (visited.has(supplierId)) {
        return 0;
      }

      const supplier = supplierById.get(supplierId);
      if (!supplier?.parent_id || !supplierById.has(supplier.parent_id)) {
        depthById.set(supplierId, 0);
        return 0;
      }

      const nextVisited = new Set(visited);
      nextVisited.add(supplierId);
      const depth: number = getDepth(supplier.parent_id, nextVisited) + 1;
      depthById.set(supplierId, depth);
      return depth;
    };

    suppliers.forEach((supplier) => {
      getDepth(supplier.id);
    });

    return depthById;
  }, [suppliers]);

  const maxVisibleDepth = useMemo(
    () => (tierFilter === 'all' ? null : Number(tierFilter)),
    [tierFilter],
  );

  const analysisDocumentsBySupplierId = useMemo(() => {
    const documentsBySupplierId = new Map<
      string,
      SupplyChainSupplierDocument[]
    >();

    analysisDocuments.forEach((document) => {
      const supplierDocuments =
        documentsBySupplierId.get(document.supplier_id) ?? [];
      supplierDocuments.push(document);
      documentsBySupplierId.set(document.supplier_id, supplierDocuments);
    });

    return documentsBySupplierId;
  }, [analysisDocuments]);

  const supplierAnalysisRows = useMemo(
    () =>
      suppliers
        .map((supplier) => ({
          supplier,
          problems: getSupplierAnalysisProblems(
            supplier,
            analysisDocumentsBySupplierId.get(supplier.id) ?? [],
          ),
        }))
        .filter((row) => row.problems.length > 0)
        .sort((leftRow, rightRow) =>
          leftRow.supplier.company_name.localeCompare(
            rightRow.supplier.company_name,
          ),
        ),
    [analysisDocumentsBySupplierId, suppliers],
  );

  const supplierStatusCounts = useMemo(() => {
    const counts = {
      total: suppliers.length,
      verified: 0,
      other: 0,
      byStatus: complianceStatusOptions.reduce<Record<string, number>>(
        (accumulator, status) => ({
          ...accumulator,
          [status]: 0,
        }),
        {},
      ),
    };

    suppliers.forEach((supplier) => {
      const status = supplier.compliance_status ?? 'Unverified';
      counts.byStatus[status] = (counts.byStatus[status] ?? 0) + 1;

      if (status === 'Verified') {
        counts.verified += 1;
      } else {
        counts.other += 1;
      }
    });

    return counts;
  }, [suppliers]);

  const supplyChainCompliancePercentage = useMemo(() => {
    if (supplierStatusCounts.total === 0) {
      return 0;
    }

    return Math.round(
      (supplierStatusCounts.verified / supplierStatusCounts.total) * 100,
    );
  }, [supplierStatusCounts]);

  const supplyChainComplianceChartData = useMemo(
    () => ({
      labels: ['Verified suppliers', 'Suppliers with other statuses'],
      datasets: [
        {
          data: [supplierStatusCounts.verified, supplierStatusCounts.other],
          backgroundColor: ['#22c55e', '#e2e8f0'],
          borderColor: ['#16a34a', '#cbd5e1'],
          borderWidth: 1,
          hoverOffset: 6,
        },
      ],
    }),
    [supplierStatusCounts],
  );

  const supplyChainComplianceChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '72%',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label(context: { label?: string; parsed: number }) {
              const total = supplierStatusCounts.total || 1;
              const percentage = Math.round((context.parsed / total) * 100);
              return `${context.label}: ${context.parsed} (${percentage}%)`;
            },
          },
        },
      },
    }),
    [supplierStatusCounts.total],
  );

  const resetCreateSupplierForm = useCallback(() => {
    setFormState(initialFormState);
    setCreateError(null);
    setEditingSupplierId(null);
    setParentSupplierSearch('');
    setShowParentSuppliersDrawer(false);
  }, []);

  const handleOpenCreateSupplierModal = useCallback(
    (parentId?: string) => {
      setSelectedSupplierId(null);
      resetCreateSupplierForm();
      if (parentId) {
        setFormState({
          ...initialFormState,
          parent_id: parentId,
          parent_ids: [parentId],
        });
      }
      setShowCreateSupplierModal(true);
    },
    [resetCreateSupplierForm],
  );

  const handleCloseCreateSupplierModal = useCallback(() => {
    setShowCreateSupplierModal(false);
    resetCreateSupplierForm();
  }, [resetCreateSupplierForm]);

  const handleOpenParentSuppliersDrawer = useCallback(() => {
    setParentSupplierSearch('');
    setShowParentSuppliersDrawer(true);
  }, []);

  const handleCloseParentSuppliersDrawer = useCallback(() => {
    setParentSupplierSearch('');
    setShowParentSuppliersDrawer(false);
  }, []);

  const handleOpenEditSupplierModal = useCallback(
    (supplierId: string) => {
      const supplier = suppliers.find((item) => item.id === supplierId);

      if (!supplier) {
        setCreateError('Supplier not found.');
        return;
      }

      const supplierParentIds = Array.from(
        new Set(
          edges
            .filter((edge) => edge.target === supplierId)
            .map((edge) => edge.source),
        ),
      );

      setCreateError(null);
      setSelectedSupplierId(null);
      setEditingSupplierId(supplier.id);
      setFormState({
        parent_id: supplierParentIds[0] ?? supplier.parent_id,
        parent_ids: supplierParentIds,
        company_name: supplier.company_name,
        registration_number: supplier.registration_number,
        duns_number: supplier.duns_number,
        email: supplier.email,
        phone: supplier.phone,
        country: supplier.country,
        city: supplier.city,
        address: supplier.address,
      });
      setParentSupplierSearch('');
      setShowCreateSupplierModal(true);
    },
    [edges, suppliers],
  );

  const handleCloseSupplierDrawer = useCallback(() => {
    setSelectedSupplierId(null);
  }, []);

  const handleOpenDeleteSupplierModal = useCallback(() => {
    setDeleteSupplierError(null);
    setDeleteWithSubSuppliers(false);
    setShowDeleteSupplierModal(true);
  }, []);

  const handleCloseDeleteSupplierModal = useCallback(() => {
    setShowDeleteSupplierModal(false);
    setDeleteSupplierError(null);
    setDeleteWithSubSuppliers(false);
  }, []);

  const handleOpenAnalyzeModal = useCallback(() => {
    setAnalyzeError(null);
    setShowAnalyzeModal(true);
  }, []);

  const handleCloseAnalyzeModal = useCallback(() => {
    setShowAnalyzeModal(false);
    setAnalyzeError(null);
  }, []);

  const handleCreateSnapshot = useCallback(async () => {
    if (!organisationId) {
      setSnapshotError('Organisation not found.');
      return;
    }

    const requestBody =
      tierFilter === 'all'
        ? { organisation_id: organisationId }
        : { organisation_id: organisationId, depth: Number(tierFilter) };

    setSnapshotLoading(true);
    setSnapshotError(null);

    try {
      const { data, error: snapshotInvokeError } =
        await supabase.functions.invoke('generate-supply-chain-report', {
          body: requestBody,
        });

      if (snapshotInvokeError) {
        throw snapshotInvokeError;
      }

      if (!data?.pdf) {
        throw new Error('Snapshot PDF URL was not returned.');
      }

      const link = document.createElement('a');
      link.href = data.pdf;
      link.download = `Supply_Chain_Snapshot_${new Date().toISOString()}.pdf`;
      link.click();
    } catch (snapshotError) {
      captureSupabaseError(snapshotError, {
        op: 'supply-chain.snapshot.create',
      });
      setSnapshotError(
        snapshotError instanceof Error
          ? snapshotError.message
          : 'Unable to create supply chain snapshot.',
      );
    } finally {
      setSnapshotLoading(false);
    }
  }, [organisationId, tierFilter]);

  const handleViewAnalyzedSupplier = useCallback((supplierId: string) => {
    setShowAnalyzeModal(false);
    setAnalyzeError(null);
    setSelectedSupplierId(supplierId);
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setStatusFilter('all');
    setTierFilter('all');
  }, []);

  const fetchAnalysisDocuments = useCallback(async () => {
    if (!organisationId) {
      setAnalysisDocuments([]);
      return;
    }

    setAnalyzeLoading(true);
    setAnalyzeError(null);

    const { data, error: fetchError } = await supabase
      .from('supply_chain_supplier_documents')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      captureSupabaseError(fetchError, { op: 'supply-chain.analysis.fetch' });
      setAnalyzeError(fetchError.message);
      setAnalysisDocuments([]);
      setAnalyzeLoading(false);
      return;
    }

    setAnalysisDocuments((data ?? []) as SupplyChainSupplierDocument[]);
    setAnalyzeLoading(false);
  }, [organisationId]);

  useEffect(() => {
    if (!showAnalyzeModal) {
      return;
    }

    void fetchAnalysisDocuments();
  }, [fetchAnalysisDocuments, showAnalyzeModal]);

  const nodeTypes = useMemo(
    () => ({
      supplier: SupplierNodeComponent,
    }),
    [],
  );

  const filteredFetchedNodes = useMemo(() => {
    if (
      !debouncedSearchQuery &&
      statusFilter === 'all' &&
      tierFilter === 'all'
    ) {
      return fetchedNodes;
    }

    return fetchedNodes.filter((node) => {
      const supplier = node.data.supplier;
      const matchesSearch =
        !debouncedSearchQuery ||
        supplier.company_name.toLowerCase().includes(debouncedSearchQuery) ||
        supplier.registration_number
          .toLowerCase()
          .includes(debouncedSearchQuery);
      const matchesStatus =
        statusFilter === 'all' || supplier.compliance_status === statusFilter;
      const supplierDepth = supplierDepthById.get(supplier.id) ?? 0;
      const matchesTier =
        maxVisibleDepth === null || supplierDepth <= maxVisibleDepth;

      return matchesSearch && matchesStatus && matchesTier;
    });
  }, [
    debouncedSearchQuery,
    fetchedNodes,
    maxVisibleDepth,
    statusFilter,
    supplierDepthById,
    tierFilter,
  ]);

  const filteredFetchedEdges = useMemo(() => {
    if (
      !debouncedSearchQuery &&
      statusFilter === 'all' &&
      tierFilter === 'all'
    ) {
      return fetchedEdges;
    }

    const visibleNodeIds = new Set(filteredFetchedNodes.map((node) => node.id));

    return fetchedEdges.filter(
      (edge) =>
        visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target),
    );
  }, [
    debouncedSearchQuery,
    fetchedEdges,
    filteredFetchedNodes,
    statusFilter,
    tierFilter,
  ]);

  const hydrateNodes = useCallback(
    (sourceNodes: SupplyChainNode[]) =>
      sourceNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onSelectSupplier: (supplierId: string) =>
            setSelectedSupplierId(supplierId),
          onAddSubSupplier: (supplierId: string) =>
            handleOpenCreateSupplierModal(supplierId),
          onEditSupplier: (supplierId: string) =>
            handleOpenEditSupplierModal(supplierId),
        },
      })),
    [handleOpenCreateSupplierModal, handleOpenEditSupplierModal],
  );

  useEffect(() => {
    setNodes(hydrateNodes(filteredFetchedNodes));
  }, [filteredFetchedNodes, hydrateNodes]);

  useEffect(() => {
    setEdges(filteredFetchedEdges);
  }, [filteredFetchedEdges]);

  const onNodesChange = useCallback(
    (changes: NodeChange<SupplyChainNode>[]) =>
      setNodes((nodesSnapshot) => applyNodeChanges(changes, nodesSnapshot)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange<SupplyChainEdge>[]) =>
      setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot)),
    [],
  );

  const getDescendantSupplierIds = useCallback(
    (supplierId: string): string[] => {
      const directChildIds = Array.from(
        new Set(
          edges
            .filter((edge) => edge.source === supplierId)
            .map((edge) => edge.target),
        ),
      );

      return directChildIds.flatMap((childId) => [
        childId,
        ...getDescendantSupplierIds(childId),
      ]);
    },
    [edges],
  );

  const onConnect = useCallback(
    async (params: Connection) => {
      const parentSupplierId = params.source;
      const childSupplierId = params.target;

      setConnectError(null);

      if (!parentSupplierId || !childSupplierId) {
        return;
      }

      if (parentSupplierId === childSupplierId) {
        setConnectError('A supplier cannot be connected to itself.');
        return;
      }

      const childSupplier = suppliers.find(
        (supplier) => supplier.id === childSupplierId,
      );

      if (!childSupplier) {
        setConnectError('Unable to find the supplier to connect.');
        return;
      }

      if (
        edges.some(
          (edge) =>
            edge.source === parentSupplierId && edge.target === childSupplierId,
        )
      ) {
        return;
      }

      const descendantIds = getDescendantSupplierIds(childSupplierId);
      if (descendantIds.includes(parentSupplierId)) {
        setConnectError('This connection would create a circular hierarchy.');
        return;
      }

      try {
        await updateSupplier(
          childSupplierId,
          {
            parent_id: parentSupplierId,
            parent_ids: [parentSupplierId],
            company_name: childSupplier.company_name,
            registration_number: childSupplier.registration_number,
            duns_number: childSupplier.duns_number,
            email: childSupplier.email,
            phone: childSupplier.phone,
            country: childSupplier.country,
            city: childSupplier.city,
            address: childSupplier.address,
          },
          { parentRelationshipMode: 'append' },
        );
      } catch (connectSupplierError) {
        captureSupabaseError(connectSupplierError, {
          op: 'supply-chain.relationship.connect',
          parentSupplierId,
          childSupplierId,
        });
        setConnectError(
          connectSupplierError instanceof Error
            ? connectSupplierError.message
            : 'Unable to connect suppliers.',
        );
      }
    },
    [edges, getDescendantSupplierIds, suppliers, updateSupplier],
  );

  const updateFormField = <K extends keyof CreateSupplyChainSupplierInput>(
    field: K,
    value: CreateSupplyChainSupplierInput[K],
  ) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const updateParentSuppliers = useCallback(
    (parentSupplierId: string, checked: boolean) => {
      setFormState((currentState) => {
        const nextParentIds = checked
          ? Array.from(new Set([...currentState.parent_ids, parentSupplierId]))
          : currentState.parent_ids.filter((id) => id !== parentSupplierId);

        return {
          ...currentState,
          parent_id: nextParentIds[0] ?? null,
          parent_ids: nextParentIds,
        };
      });
    },
    [],
  );

  const handleCreateSupplier = async (event: React.FormEvent) => {
    event.preventDefault();

    if (
      !formState.company_name.trim() ||
      !formState.registration_number.trim()
    ) {
      setCreateError('Company name and registration number are required.');
      return;
    }

    setCreateError(null);

    try {
      const payload = {
        ...formState,
        parent_ids: Array.from(new Set(formState.parent_ids)),
        parent_id: formState.parent_ids[0] ?? null,
        company_name: formState.company_name.trim(),
        registration_number: formState.registration_number.trim(),
        duns_number: formState.duns_number?.trim() || null,
        email: formState.email?.trim() || null,
        phone: formState.phone?.trim() || null,
        country: formState.country?.trim() || null,
        city: formState.city?.trim() || null,
        address: formState.address?.trim() || null,
      };

      if (editingSupplierId) {
        await updateSupplier(editingSupplierId, payload);
      } else {
        await createSupplier(payload);
      }

      handleCloseCreateSupplierModal();
    } catch (creationError) {
      const message =
        creationError instanceof Error
          ? creationError.message
          : editingSupplierId
            ? 'Unable to update supplier.'
            : 'Unable to create supplier.';
      setCreateError(message);
    }
  };

  const handleDeleteSupplier = useCallback(async () => {
    if (!selectedSupplier || !selectedOrganisation?.organisation?.id) {
      setDeleteSupplierError('Supplier or organisation not found.');
      return;
    }

    setDeletingSupplier(true);
    setDeleteSupplierError(null);

    try {
      if (deleteWithSubSuppliers) {
        const descendantIds = getDescendantSupplierIds(selectedSupplier.id);
        const idsToDelete = [selectedSupplier.id, ...descendantIds];

        const { error: deleteError } = await supabase
          .from('supply_chain_suppliers')
          .delete()
          .in('id', idsToDelete)
          .eq('organisation_id', selectedOrganisation.organisation.id);

        if (deleteError) {
          throw deleteError;
        }
      } else {
        if (selectedSupplierChildIds.length > 0) {
          const { error: reparentError } = await supabase
            .from('supply_chain_supplier_relationships')
            .delete()
            .in('supplier_id', selectedSupplierChildIds)
            .eq('parent_supplier_id', selectedSupplier.id)
            .eq('organisation_id', selectedOrganisation.organisation.id);

          if (reparentError) {
            throw reparentError;
          }
        }

        const { error: deleteError } = await supabase
          .from('supply_chain_suppliers')
          .delete()
          .eq('id', selectedSupplier.id)
          .eq('organisation_id', selectedOrganisation.organisation.id);

        if (deleteError) {
          throw deleteError;
        }
      }

      handleCloseDeleteSupplierModal();
      handleCloseSupplierDrawer();
      await refetch();
    } catch (deleteError) {
      captureSupabaseError(deleteError, { op: 'supply-chain.supplier.delete' });
      setDeleteSupplierError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Unable to delete supplier.',
      );
    } finally {
      setDeletingSupplier(false);
    }
  }, [
    deleteWithSubSuppliers,
    getDescendantSupplierIds,
    handleCloseDeleteSupplierModal,
    handleCloseSupplierDrawer,
    refetch,
    selectedOrganisation?.organisation?.id,
    selectedSupplier,
    selectedSupplierChildIds,
  ]);

  return (
    <Container
      fluid
      className="d-flex flex-column"
      style={{ height: 'calc(100vh - 7rem)', minHeight: 0 }}
    >
      <h3 className="mb-4">Supply Chain</h3>
      <p>
        This is a map of your supply chain. You can add suppliers, connect them,
        and visualize the relationships between them.
      </p>
      {!hasSupplyChainMapping ? (
        <div className="mb-4 rounded-4 border border-primary bg-white p-4">
          <h5 className="mb-3 text-primary">
            Upgrade to Supply Chain Management
          </h5>
          <p className="mb-3">
            Supply chain mapping is disabled in your current plan. Upgrade your
            plan to unlock supplier mapping, relationship visualization, and
            supply chain analysis tools.
          </p>
          <Link to="/settings?tab=organisation">
            <Button variant="outline-primary">Upgrade Now</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-3 rounded-4 border bg-white p-3">
            {snapshotError && (
              <p className="text-danger mb-3">{snapshotError}</p>
            )}
            {connectError && <p className="text-danger mb-3">{connectError}</p>}
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="d-flex flex-grow-1 flex-wrap align-items-center gap-3">
                <div className="flex-grow-1" style={{ maxWidth: '360px' }}>
                  <Form.Control
                    type="search"
                    placeholder="Search by company name or reg. number"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className={hasActiveFilters ? 'border-primary' : undefined}
                  />
                </div>
                <div style={{ width: '220px', maxWidth: '100%' }}>
                  <Form.Select
                    aria-label="Filter suppliers by compliance status"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className={hasActiveFilters ? 'border-primary' : undefined}
                  >
                    <option value="all">All statuses</option>
                    {complianceStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="outline-primary"
                    onClick={handleResetFilters}
                  >
                    Reset
                  </Button>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  onClick={handleCreateSnapshot}
                  disabled={snapshotLoading}
                >
                  {snapshotLoading ? 'Creating report...' : 'Create report'}
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={handleOpenAnalyzeModal}
                >
                  Analyze
                </Button>
                <Button onClick={() => handleOpenCreateSupplierModal()}>
                  Add supplier
                </Button>
              </div>
            </div>
          </div>
          {loading && <p className="text-muted">Loading supply chain...</p>}
          {error && (
            <p className="text-danger">Error loading supply chain: {error}</p>
          )}
          <div
            className="position-relative flex-grow-1 border rounded-4 overflow-hidden bg-white"
            style={{ minHeight: 0, height: '100%' }}
          >
            <div
              className={`position-absolute top-0 start-0 m-3 rounded-4 border bg-white shadow-sm ${tierFilter !== 'all' ? 'border-primary' : 'border-light'}`}
              style={{ zIndex: 5, width: '72px' }}
            >
              <div className="px-2 pt-2 text-center">
                <Form.Label className="mb-0 small text-muted">Depth</Form.Label>
              </div>
              <div className="d-flex flex-column align-items-center px-2 pb-2 pt-1">
                <span className="small fw-semibold text-uppercase mb-1">
                  {tierFilter === 'all' ? 'All' : tierFilter}
                </span>
                <div className="d-flex align-items-stretch justify-content-center gap-2">
                  <Form.Range
                    min={0}
                    max={4}
                    step={1}
                    value={tierFilter === 'all' ? 4 : Number(tierFilter)}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      setTierFilter(
                        nextValue === 4
                          ? 'all'
                          : (String(nextValue) as TierFilterValue),
                      );
                    }}
                    aria-label="Limit displayed supplier child tiers"
                    style={{
                      writingMode: 'vertical-lr',
                      WebkitAppearance: 'slider-vertical',
                      width: '18px',
                      height: '132px',
                      margin: 0,
                    }}
                  />
                </div>
              </div>
            </div>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              //   defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 1, minZoom: 0.3 }}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
            >
              <Controls />
              <MiniMap />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>
          </div>

          <Offcanvas
            show={Boolean(selectedSupplier)}
            onHide={handleCloseSupplierDrawer}
            placement="end"
            style={{ width: '520px', maxWidth: '100%' }}
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>Supplier details</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              {selectedSupplier ? (
                <div className="d-flex flex-column gap-4">
                  <div>
                    <h4 className="mb-1">{selectedSupplier.company_name}</h4>
                    <p className="text-muted mb-0 d-flex align-items-center gap-2">
                      <i
                        className="bi bi-circle-fill"
                        style={{
                          color: selectedSupplierStatusColor,
                          fontSize: '0.75rem',
                        }}
                        aria-label={`Supplier status: ${selectedSupplierStatus}`}
                        title={selectedSupplierStatus}
                      ></i>
                      <span>{selectedSupplierStatus}</span>
                    </p>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      onClick={() =>
                        handleOpenEditSupplierModal(selectedSupplier.id)
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() =>
                        handleOpenCreateSupplierModal(selectedSupplier.id)
                      }
                    >
                      Add sub-supplier
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={handleOpenDeleteSupplierModal}
                    >
                      Delete
                    </Button>
                  </div>

                  <Tabs
                    key={selectedSupplier.id}
                    defaultActiveKey="profile"
                    id="supplier-drawer-tabs"
                  >
                    <Tab eventKey="profile" title="Profile">
                      <div className="d-flex flex-column gap-3 pt-3">
                        <div>
                          <div className="small text-muted">
                            Registration number
                          </div>
                          <div>{selectedSupplier.registration_number}</div>
                        </div>
                        <div>
                          <div className="small text-muted">D-U-N-S Number</div>
                          <div>
                            {selectedSupplier.duns_number ?? 'Not provided'}
                          </div>
                        </div>
                        <div>
                          <div className="small text-muted">
                            Parent supplier
                          </div>
                          <div>
                            {selectedSupplierParents.length === 0
                              ? 'No parent supplier'
                              : selectedSupplierParents.length === 1
                                ? selectedSupplierParents[0].company_name
                                : 'Managed using supplier relationships'}
                          </div>
                        </div>
                        <div>
                          <div className="small text-muted">Email</div>
                          <div>{selectedSupplier.email ?? 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="small text-muted">Phone</div>
                          <div>{selectedSupplier.phone ?? 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="small text-muted">Country</div>
                          <div>
                            {selectedSupplier.country ?? 'Not provided'}
                          </div>
                        </div>
                        <div>
                          <div className="small text-muted">City</div>
                          <div>{selectedSupplier.city ?? 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="small text-muted">Address</div>
                          <div>
                            {selectedSupplier.address ?? 'Not provided'}
                          </div>
                        </div>
                      </div>
                    </Tab>
                    <Tab eventKey="compliance" title="Compliance">
                      <SupplierComplianceDocuments
                        supplierId={selectedSupplier.id}
                        supplier={selectedSupplier}
                        onSupplierUpdated={refetch}
                      />
                    </Tab>
                  </Tabs>
                </div>
              ) : null}
            </Offcanvas.Body>
          </Offcanvas>

          <Modal
            show={showCreateSupplierModal}
            onHide={handleCloseCreateSupplierModal}
            centered
            size="lg"
            contentClassName="position-relative overflow-hidden"
          >
            <Form onSubmit={handleCreateSupplier} autoComplete="off">
              <Modal.Header closeButton>
                <Modal.Title>
                  {editingSupplierId ? 'Edit supplier' : 'Add supplier'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {createError && <p className="text-danger">{createError}</p>}
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group
                      className="mb-0"
                      controlId="supplyChainParentSupplier"
                    >
                      <Form.Label>Parent supplier</Form.Label>
                      {editingSupplierId ? (
                        <div className="d-flex flex-column gap-2 rounded-3 border px-3 py-2">
                          <div className="small text-muted">
                            {selectedFormParentSupplierNames.length > 0
                              ? selectedFormParentSupplierNames.join(', ')
                              : 'No parent suppliers'}
                          </div>
                          <div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={handleOpenParentSuppliersDrawer}
                              disabled={saving}
                            >
                              Edit
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <DropdownSelect
                          options={parentSupplierOptions}
                          selectedIds={formState.parent_id ?? '__none__'}
                          onValueChanged={(selected) => {
                            const option = selected as DropdownSelectOption;
                            const nextParentId =
                              option.id === '__none__' ? null : option.id;

                            setFormState((currentState) => ({
                              ...currentState,
                              parent_id: nextParentId,
                              parent_ids: nextParentId ? [nextParentId] : [],
                            }));
                          }}
                          placeholder="No parent supplier"
                          full
                          disabled={saving}
                        />
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-0"
                      controlId="supplyChainCompanyName"
                    >
                      <Form.Label>Company name</Form.Label>
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        value={formState.company_name}
                        onChange={(event) =>
                          updateFormField('company_name', event.target.value)
                        }
                        disabled={saving}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-0"
                      controlId="supplyChainRegistrationNumber"
                    >
                      <Form.Label>Registration number</Form.Label>
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        value={formState.registration_number}
                        onChange={(event) =>
                          updateFormField(
                            'registration_number',
                            event.target.value,
                          )
                        }
                        disabled={saving}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-0"
                      controlId="supplyChainDunsNumber"
                    >
                      <Form.Label>D-U-N-S Number (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        value={formState.duns_number ?? ''}
                        onChange={(event) =>
                          updateFormField(
                            'duns_number',
                            event.target.value || null,
                          )
                        }
                        disabled={saving}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0" controlId="supplyChainEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        autoComplete="off"
                        value={formState.email ?? ''}
                        onChange={(event) =>
                          updateFormField('email', event.target.value || null)
                        }
                        disabled={saving}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0" controlId="supplyChainPhone">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        value={formState.phone ?? ''}
                        onChange={(event) =>
                          updateFormField('phone', event.target.value || null)
                        }
                        disabled={saving}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0" controlId="supplyChainCountry">
                      <Form.Label>Country</Form.Label>
                      <CountrySelect
                        value={formState.country ?? ''}
                        onChange={(value) =>
                          updateFormField(
                            'country',
                            typeof value === 'string' ? value || null : null,
                          )
                        }
                        full
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0" controlId="supplyChainCity">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        value={formState.city ?? ''}
                        onChange={(event) =>
                          updateFormField('city', event.target.value || null)
                        }
                        disabled={saving}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group className="mb-0" controlId="supplyChainAddress">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        autoComplete="off"
                        value={formState.address ?? ''}
                        onChange={(event) =>
                          updateFormField('address', event.target.value || null)
                        }
                        disabled={saving}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-secondary"
                  onClick={handleCloseCreateSupplierModal}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving
                    ? editingSupplierId
                      ? 'Saving supplier...'
                      : 'Creating supplier...'
                    : editingSupplierId
                      ? 'Save supplier'
                      : 'Create supplier'}
                </Button>
              </Modal.Footer>

              {editingSupplierId ? (
                <>
                  <div
                    className={`position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-25 ${
                      showParentSuppliersDrawer ? 'd-block' : 'd-none'
                    }`}
                    onClick={handleCloseParentSuppliersDrawer}
                  />
                  <div
                    className="position-absolute top-0 end-0 h-100 bg-white border-start shadow-lg d-flex flex-column"
                    style={{
                      width: 'min(100%, 360px)',
                      transform: showParentSuppliersDrawer
                        ? 'translateX(0)'
                        : 'translateX(100%)',
                      transition: 'transform 0.25s ease',
                      zIndex: 1056,
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-3">
                      <div className="fw-semibold">Parent suppliers</div>
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none"
                        onClick={handleCloseParentSuppliersDrawer}
                        disabled={saving}
                      >
                        Close
                      </Button>
                    </div>
                    <div className="d-flex flex-column gap-3 p-3 h-100">
                      <Form.Control
                        type="search"
                        placeholder="Search suppliers"
                        value={parentSupplierSearch}
                        onChange={(event) =>
                          setParentSupplierSearch(event.target.value)
                        }
                        disabled={saving}
                      />
                      <div
                        className="border rounded-3 p-2 flex-grow-1"
                        style={{ overflowY: 'auto' }}
                      >
                        {filteredEditableParentSuppliers.length > 0 ? (
                          filteredEditableParentSuppliers.map((supplier) => (
                            <Form.Check
                              key={supplier.id}
                              id={`parent-supplier-${supplier.id}`}
                              className="py-1"
                              type="checkbox"
                              label={supplier.company_name}
                              checked={formState.parent_ids.includes(
                                supplier.id,
                              )}
                              onChange={(event) =>
                                updateParentSuppliers(
                                  supplier.id,
                                  event.target.checked,
                                )
                              }
                              disabled={saving}
                            />
                          ))
                        ) : (
                          <div className="small text-muted px-1 py-2">
                            No suppliers match this search.
                          </div>
                        )}
                      </div>
                      <div>
                        <Button
                          variant="outline-secondary"
                          onClick={handleCloseParentSuppliersDrawer}
                          disabled={saving}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </Form>
          </Modal>

          <Modal
            show={showAnalyzeModal}
            onHide={handleCloseAnalyzeModal}
            centered
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Supply Chain analysis</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {analyzeLoading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <Spinner animation="border" role="status" />
                </div>
              ) : analyzeError ? (
                <p className="text-danger mb-0">{analyzeError}</p>
              ) : (
                <div className="d-flex flex-column gap-4">
                  <div className="rounded-4 border bg-light-subtle p-4">
                    <Row className="g-4 align-items-center">
                      <Col md={5}>
                        <div
                          className="position-relative mx-auto"
                          style={{ width: '220px', height: '220px' }}
                        >
                          <Doughnut
                            data={supplyChainComplianceChartData}
                            options={supplyChainComplianceChartOptions}
                          />
                          <div className="position-absolute top-50 start-50 translate-middle text-center">
                            <div className="display-6 fw-semibold lh-1">
                              {supplyChainCompliancePercentage}%
                            </div>
                            <div className="small text-muted mt-1">
                              Supply chain
                              <br />
                              compliance
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={7}>
                        <div className="d-flex flex-column gap-3">
                          <div>
                            <div className="text-uppercase small text-muted mb-1">
                              Overview
                            </div>
                            <h5 className="mb-2">Supply Chain analysis</h5>
                            <p className="text-muted mb-0">
                              {supplierStatusCounts.verified} of{' '}
                              {supplierStatusCounts.total} suppliers are
                              currently verified. The remaining{' '}
                              {supplierStatusCounts.other} need review, updated
                              documents, or risk remediation.
                            </p>
                          </div>
                          <Row className="g-3">
                            <Col sm={4}>
                              <div className="rounded-4 border bg-white p-3 h-100">
                                <div className="small text-muted mb-1">
                                  Total suppliers
                                </div>
                                <div className="h4 mb-0">
                                  {supplierStatusCounts.total}
                                </div>
                              </div>
                            </Col>
                            <Col sm={4}>
                              <div className="rounded-4 border bg-white p-3 h-100">
                                <div className="small text-muted mb-1">
                                  Verified
                                </div>
                                <div className="h4 mb-0 text-success">
                                  {supplierStatusCounts.verified}
                                </div>
                              </div>
                            </Col>
                            <Col sm={4}>
                              <div className="rounded-4 border bg-white p-3 h-100">
                                <div className="small text-muted mb-1">
                                  With problems
                                </div>
                                <div className="h4 mb-0 text-warning-emphasis">
                                  {supplierAnalysisRows.length}
                                </div>
                              </div>
                            </Col>
                          </Row>
                          <div className="d-flex flex-wrap gap-2">
                            {complianceStatusOptions.map((status) => (
                              <div
                                key={status}
                                className="d-flex align-items-center gap-2 rounded-pill border bg-white px-3 py-2 small"
                              >
                                <span
                                  className="rounded-circle"
                                  style={{
                                    width: '0.65rem',
                                    height: '0.65rem',
                                    backgroundColor:
                                      statusColorBySupplierStatus[status],
                                  }}
                                />
                                <span>{status}</span>
                                <span className="text-muted">
                                  {supplierStatusCounts.byStatus[status] ?? 0}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div className="rounded-4 border overflow-hidden">
                    <div className="d-flex align-items-center justify-content-between border-bottom bg-body-tertiary px-4 py-3">
                      <div>
                        <h6 className="mb-1">Suppliers with issues</h6>
                        <p className="text-muted small mb-0">
                          Review suppliers that are unverified, expired,
                          flagged, or have risk warnings.
                        </p>
                      </div>
                    </div>
                    {supplierAnalysisRows.length === 0 ? (
                      <div className="px-4 py-5 text-center text-muted">
                        No supplier issues were found for the current
                        organisation.
                      </div>
                    ) : (
                      <Table responsive hover className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Supplier</th>
                            <th>Problems</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {supplierAnalysisRows.map(
                            ({ supplier, problems }) => (
                              <tr key={supplier.id}>
                                <td className="fw-medium">
                                  {supplier.company_name}
                                </td>
                                <td>
                                  <ul className="mb-0 ps-3">
                                    {problems.map((problem) => (
                                      <li key={`${supplier.id}-${problem.key}`}>
                                        {problem.label}
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                                <td className="text-end">
                                  <Button
                                    variant="link"
                                    className="p-0"
                                    onClick={() =>
                                      handleViewAnalyzedSupplier(supplier.id)
                                    }
                                  >
                                    View
                                  </Button>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </div>
              )}
            </Modal.Body>
          </Modal>

          <Modal
            show={showDeleteSupplierModal}
            onHide={handleCloseDeleteSupplierModal}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Delete supplier</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {deleteSupplierError && (
                <p className="text-danger mb-3">{deleteSupplierError}</p>
              )}
              <p className="mb-3">
                Are you sure you want to delete{' '}
                <strong>
                  {selectedSupplier?.company_name ?? 'this supplier'}
                </strong>
                ?
              </p>
              {selectedSupplierChildIds.length > 0 && (
                <Form.Check
                  type="checkbox"
                  id="delete-sub-suppliers"
                  checked={deleteWithSubSuppliers}
                  onChange={(event) =>
                    setDeleteWithSubSuppliers(event.target.checked)
                  }
                  label="Also delete sub-suppliers"
                  disabled={deletingSupplier}
                />
              )}
              {selectedSupplierChildIds.length > 0 &&
                !deleteWithSubSuppliers && (
                  <p className="small text-muted mb-0 mt-3">
                    If unchecked, direct sub-suppliers will be kept and moved to
                    the top level.
                  </p>
                )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={handleCloseDeleteSupplierModal}
                disabled={deletingSupplier}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteSupplier}
                disabled={deletingSupplier}
              >
                {deletingSupplier ? 'Deleting...' : 'Delete supplier'}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default SupplyChain;
